/**
 * DataTableSection — fetches records from a bound table and renders an HTML table.
 * Supports: section title/description, table style, header colors, Show More button.
 */

import React from 'react';
import { usePublicTableList } from '../../../hooks/usePublicTableView';
import DataSectionWrapper from '../DataSectionWrapper';

export default function DataTableSection({ config, dataBinding, theme }) {
  const cfg       = config || {};
  const tableSlug = dataBinding?.table_slug;
  const limit     = dataBinding?.limit || 25;

  const tableStyleCls =
    cfg.table_style === 'striped'  ? 'table table-striped table-hover mb-0'
    : cfg.table_style === 'bordered' ? 'table table-bordered mb-0'
    : cfg.table_style === 'compact'  ? 'table table-sm table-hover mb-0'
    : cfg.table_style === 'minimal'  ? 'table mb-0'
    : 'table table-hover mb-0';

  const headerBg   = cfg.header_bg_color   || '#f8fafc';
  const headerText = cfg.header_text_color  || '#374151';

  const { data, isLoading, isError } = usePublicTableList(tableSlug, { page_size: limit });
  const records = data?.records || data?.results || [];
  const columns = records.length > 0 ? Object.keys(records[0]) : [];

  if (!tableSlug) return null;

  if (isLoading) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-muted text-center py-4">
          <span className="spinner-border spinner-border-sm me-2" />Loading…
        </div>
      </DataSectionWrapper>
    );
  }

  if (isError) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-muted text-center py-4">Failed to load data.</div>
      </DataSectionWrapper>
    );
  }

  if (records.length === 0) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-muted text-center py-4">No records found.</div>
      </DataSectionWrapper>
    );
  }

  return (
    <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
      <div
        className="table-responsive rounded"
        style={{
          border: cfg.table_style === 'minimal' ? 'none' : '1px solid #e2e8f0',
          borderRadius: 'var(--card-radius, 8px)',
          overflow: 'hidden',
        }}
      >
        <table className={tableStyleCls}>
          <thead>
            <tr style={{ background: headerBg }}>
              {cfg.show_row_numbers && (
                <th style={{ color: headerText, padding: '8px 12px', fontSize: 13, fontWeight: 600, width: 40 }}>#</th>
              )}
              {columns.map(col => (
                <th key={col} style={{ color: headerText, padding: '8px 12px', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((row, i) => (
              <tr key={i}>
                {cfg.show_row_numbers && (
                  <td style={{ padding: '8px 12px', fontSize: 13, color: '#94a3b8' }}>{i + 1}</td>
                )}
                {columns.map(col => (
                  <td key={col} style={{ padding: '8px 12px', fontSize: 13, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row[col] !== null && row[col] !== undefined ? String(row[col]) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DataSectionWrapper>
  );
}
